import React, { useEffect, useState } from "react"
import BreakForm from "../../Form/BreakForm"
import DatePicker from "../../Form/DatePicker"
import Select from "../../Form/Select"
import CircleLoading from "../../CircleLoading"
import api from "../../../services/api"
import { toast } from "react-toastify"
import moment from "moment"
import {
    MdOutlineAdsClick,
    MdOutlineAssignmentTurnedIn,
    MdOutlineCalendarToday,
    MdOutlineErrorOutline,
    MdOutlineInsights,
    MdOutlinePayments,
    MdOutlineRoute,
    MdOutlineTimer,
    MdOutlineTrendingUp,
} from "react-icons/md"
import styles from "./journey.module.css"

const emptyJourney = { events: [], summary: {}, health: {}, attribution: {}, stageTimeline: [], nextAction: {}, insights: [] }

const formatDateTime = (date) => date ? moment(date).format("DD/MM/YYYY HH:mm") : "-"
const formatDuration = (hours) => {
    if (hours === null || hours === undefined) return "-"
    if (hours < 24) return `${Math.max(1, Math.round(hours))}h`
    return `${Math.round(hours / 24)}d`
}
const formatFirstInteraction = (minutes) => {
    if (minutes === null || minutes === undefined) return "Sem registro"
    if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`
    if (minutes < 1440) return `${Math.round(minutes / 60)} h`
    return `${Math.round(minutes / 1440)} d`
}

const insightIcon = {
    positive: <MdOutlineTrendingUp />,
    warning: <MdOutlineErrorOutline />,
    danger: <MdOutlineErrorOutline />,
    info: <MdOutlineInsights />,
    neutral: <MdOutlineInsights />,
}

const Journey = ({ lead_id }) => {
    const [journey, setJourney] = useState(emptyJourney)
    const [filters, setFilters] = useState({})
    const [eventTypes, setEventTypes] = useState([])
    const [initialLoading, setInitialLoading] = useState(false)

    useEffect(() => {
        getJourney()
    }, [])

    useEffect(() => {
        const types = []
        journey.events?.forEach(event => {
            if (!types.some(type => type.id === event.event_type)) {
                types.push({ id: event.event_type, value: event.title })
            }
        })
        setEventTypes(types.sort((a, b) => a.value.localeCompare(b.value)))
    }, [journey.events])

    const getJourney = () => {
        setInitialLoading(true)
        api.get(`/leads/${lead_id}/journey`).then(response => {
            setJourney({ ...emptyJourney, ...(response?.data?.data || {}) })
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setInitialLoading(false))
    }

    const onChangeFilters = (name, value) => {
        if (name === "period") {
            setFilters(previousValue => ({ ...previousValue, dateStart: value.dateStart, dateEnd: value.dateEnd }))
            return
        }
        setFilters(previousValue => ({ ...previousValue, [name]: value }))
    }

    const filterEvent = (event) => {
        const eventDate = moment(event.occurred_at).format("YYYY-MM-DD")
        const validPeriod = (!filters.dateStart || moment(filters.dateStart).isSameOrBefore(eventDate)) && (!filters.dateEnd || moment(filters.dateEnd).isSameOrAfter(eventDate))
        const validType = !filters?.eventTypes?.length || filters.eventTypes.includes(event.event_type)
        return validPeriod && validType
    }

    const filteredEvents = journey.events?.filter(filterEvent) || []
    const attribution = journey.attribution || {}
    const health = journey.health || {}
    const stageTimeline = journey.stageTimeline || []
    const nextAction = journey.nextAction || {}

    return (
        <div className={styles.journey}>
            <BreakForm title="Jornada do lead" showAction={false} />

            {initialLoading && <CircleLoading />}

            {!initialLoading &&
                <>
                    <section className={styles.hero}>
                        <div>
                            <span>{health.status_label || "Jornada"}</span>
                            <strong>{health.lead_name || "Lead"}</strong>
                            <small>{health.current_stage || "Sem etapa"} · {health.origin || "Origem não informada"} · {health.temperature?.label || "Sem temperatura"}</small>
                        </div>
                        <div className={styles.sideStatus}>
                            <div className={`${styles.temperature} ${styles[health.temperature?.id] || ""}`}>
                                <MdOutlineTimer />
                                <div>
                                    <span>Status do lead</span>
                                    <strong>{health.temperature?.label || "Sem status"}</strong>
                                    <small>{health.temperature?.description || "Baseado na última atividade e no prazo da etapa."}</small>
                                </div>
                            </div>
                            <div className={styles.nextAction}>
                            <MdOutlineAssignmentTurnedIn />
                            <div>
                                <span>Próxima ação</span>
                                <strong>{nextAction.title || "Sem ação pendente"}</strong>
                                <small>{nextAction.description || "Acompanhe a evolução no histórico."}</small>
                            </div>
                            </div>
                        </div>
                    </section>

                    <section className={styles.metricGrid}>
                        <div className={styles.metricCard}>
                            <MdOutlineTimer />
                            <span>Primeira interação</span>
                            <strong>{formatFirstInteraction(health.first_interaction_minutes)}</strong>
                            <small>{health.first_interaction_at ? formatDateTime(health.first_interaction_at) : "Ainda não registrada"}</small>
                        </div>
                        <div className={styles.metricCard}>
                            <MdOutlineCalendarToday />
                            <span>Tempo em aberto</span>
                            <strong>{health.open_days || 0} dias</strong>
                            <small>Última atividade há {health.inactive_days || 0} dias</small>
                        </div>
                        <div className={styles.metricCard}>
                            <MdOutlinePayments />
                            <span>Receita</span>
                            <strong>{health.revenue_formatted || "R$ 0,00"}</strong>
                            <small>{health.has_sale ? "Venda registrada" : "Sem venda registrada"}</small>
                        </div>
                        <div className={`${styles.metricCard} ${health.tasks_overdue > 0 ? styles.warning : ""}`}>
                            <MdOutlineAssignmentTurnedIn />
                            <span>Tarefas</span>
                            <strong>{health.tasks_open || 0} abertas</strong>
                            <small>{health.tasks_overdue || 0} atrasadas</small>
                        </div>
                    </section>

                    <section className={styles.analysisGrid}>
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <MdOutlineAdsClick />
                                <div>
                                    <strong>Marketing e resultado</strong>
                                    <span>{attribution.has_marketing ? "Campanha atribuída ao lead" : "Sem campanha atribuída"}</span>
                                </div>
                            </div>
                            <div className={styles.marketingGrid}>
                                <div><span>Canal</span><strong>{attribution.channel_label || attribution.channel || "-"}</strong></div>
                                <div><span>Campanha</span><strong>{attribution.campaign || "-"}</strong></div>
                                <div><span>Conjunto</span><strong>{attribution.adset || "-"}</strong></div>
                                <div><span>Criativo/anúncio</span><strong>{attribution.creative || attribution.ad || "-"}</strong></div>
                                <div><span>Investimento</span><strong>{attribution.spend_formatted || "-"}</strong></div>
                                <div><span>ROAS / ROI</span><strong>{attribution.roas_formatted || "-"} / {attribution.roi_formatted || "-"}</strong></div>
                            </div>
                            {(attribution.thumbnail_url || attribution.creative_url) &&
                                <a className={styles.creativeLink} href={attribution.creative_url || attribution.thumbnail_url} target="_blank" rel="noreferrer">Ver criativo</a>
                            }
                        </div>

                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <MdOutlineInsights />
                                <div>
                                    <strong>Leitura inteligente</strong>
                                    <span>Alertas e pontos importantes</span>
                                </div>
                            </div>
                            <div className={styles.insights}>
                                {(journey.insights || []).map((insight, index) => (
                                    <div className={`${styles.insight} ${styles[insight.type] || ""}`} key={`${insight.title}-${index}`}>
                                        {insightIcon[insight.type] || insightIcon.info}
                                        <div>
                                            <strong>{insight.title}</strong>
                                            <span>{insight.description}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <MdOutlineRoute />
                            <div>
                                <strong>Caminho no funil</strong>
                                <span>Etapas percorridas e tempo aproximado</span>
                            </div>
                        </div>
                        <div className={styles.stagePath}>
                            {stageTimeline.map(stage => (
                                <div className={`${styles.stageStep} ${stage.current ? styles.current : ""}`} key={stage.event_id}>
                                    <strong>{stage.stage}</strong>
                                    <span>{formatDuration(stage.duration_hours)}</span>
                                    <small>{formatDateTime(stage.started_at)}</small>
                                </div>
                            ))}
                            {!stageTimeline.length && <span className={styles.emptyText}>Nenhuma movimentação de etapa registrada.</span>}
                        </div>
                    </section>

                    {journey.events?.length > 0 &&
                        <div className={styles.filters}>
                            <DatePicker name="Período" dateStart={filters.dateStart} dateEnd={filters.dateEnd} onChange={(dateStart, dateEnd) => onChangeFilters("period", { dateStart, dateEnd })} multiple={true} />
                            <Select name="Evento" placeholder="Selecione o evento" selected={filters.eventTypes} options={eventTypes} onChange={(value) => onChangeFilters("eventTypes", value)} multiple={true} />
                        </div>
                    }

                    <div className={styles.summary}>
                        <span>{journey.summary?.total || 0} eventos</span>
                        <span>{Object.keys(journey.summary?.by_source || {}).length || 0} canais</span>
                        <span>{attribution.touchpoints || 0} pontos de atribuição</span>
                    </div>

                    <div className={styles.body}>
                        {filteredEvents.map(event => (
                            <div className={styles.card} key={event._id}>
                                <div className={styles.header}>
                                    <div className={styles.titleGroup}>
                                        <MdOutlineRoute />
                                        <div>
                                            <strong>{event.title}</strong>
                                            <div className={styles.eventMeta}>
                                                <span>Evento: {event.source_label || event.source || "Sistema"}</span>
                                                {event.user_name && <span>Usuário: {event.user_name}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.date}>
                                        <MdOutlineCalendarToday />
                                        <span>{moment(event.occurred_at).format("DD/MM/YYYY HH:mm:ss")}</span>
                                    </div>
                                </div>
                                {event.details?.length > 0 &&
                                    <div className={styles.details}>
                                        {event.details.map(detail => <span key={detail}>{detail}</span>)}
                                    </div>
                                }
                            </div>
                        ))}
                        {!journey.events?.length && <span>Nenhum evento de jornada registrado.</span>}
                        {journey.events?.length > 0 && !filteredEvents.length && <span>Nenhum resultado para sua busca.</span>}
                    </div>
                </>
            }
        </div>
    )
}

export default Journey
